export const WORKSPACE_DEFAULT_WIDTH = 100;
export const WORKSPACE_DEFAULT_HEIGHT = 50;

export interface CollisionRect {
  x: number;
  y: number;
  width: number;
  height: number;
  rotateDegree: number;
}

export interface CollisionWorkspace extends CollisionRect {
  id: string;
  number?: number | string;
}

type Point = {x: number; y: number};

const getRotatedRect = (
  rectX: number,
  rectY: number,
  rectWidth: number,
  rectHeight: number,
  rotate: number,
): Point[] => {
  const cx = rectX + rectWidth / 2;
  const cy = rectY + rectHeight / 2;
  const rad = (rotate * Math.PI) / 180;
  const corners = [
    {x: rectX, y: rectY},
    {x: rectX + rectWidth, y: rectY},
    {x: rectX + rectWidth, y: rectY + rectHeight},
    {x: rectX, y: rectY + rectHeight},
  ];

  return corners.map((point) => {
    const dx = point.x - cx;
    const dy = point.y - cy;
    return {
      x: cx + dx * Math.cos(rad) - dy * Math.sin(rad),
      y: cy + dx * Math.sin(rad) + dy * Math.cos(rad),
    };
  });
};

const getAxes = (points: Point[]) => {
  const axes = [];
  for (let i = 0; i < points.length; i++) {
    const p1 = points[i];
    const p2 = points[(i + 1) % points.length];
    const edge = {x: p2.x - p1.x, y: p2.y - p1.y};
    const normal = {x: -edge.y, y: edge.x};
    const length = Math.hypot(normal.x, normal.y);
    axes.push({x: normal.x / length, y: normal.y / length});
  }
  return axes;
};

const project = (points: Point[], axis: Point) => {
  const projections = points.map((p) => p.x * axis.x + p.y * axis.y);
  return {
    min: Math.min(...projections),
    max: Math.max(...projections),
  };
};

const isOverlap = (a: {max: number; min: number}, b: {max: number; min: number}) => a.max >= b.min && b.max >= a.min;

export const areRotatedRectsColliding = (rect1: CollisionRect, rect2: CollisionRect): boolean => {
  const points1 = getRotatedRect(rect1.x, rect1.y, rect1.width, rect1.height, rect1.rotateDegree);
  const points2 = getRotatedRect(rect2.x, rect2.y, rect2.width, rect2.height, rect2.rotateDegree);
  const axes = [...getAxes(points1), ...getAxes(points2)];

  for (const axis of axes) {
    const p1 = project(points1, axis);
    const p2 = project(points2, axis);
    if (!isOverlap(p1, p2)) {
      return false;
    }
  }

  return true;
};

export const findCollidingWorkspace = (
  candidate: CollisionWorkspace,
  others: CollisionWorkspace[],
): CollisionWorkspace | undefined => {
  return others.find((workspace) => workspace.id !== candidate.id && areRotatedRectsColliding(candidate, workspace));
};

export const coordinatesToTopLeft = (
  coordinates: Array<number[]>,
  width: number = WORKSPACE_DEFAULT_WIDTH,
  height: number = WORKSPACE_DEFAULT_HEIGHT,
): {x: number; y: number} => ({
  x: coordinates[0][0] - width / 2,
  y: coordinates[0][1] - height / 2,
});
